import { Box } from "@mui/material";
import { graphviz } from "d3-graphviz";
import { useEffect, useMemo, useRef } from "react";

let counter = 0;
const getId = () => `graph${counter++}`;

/**
 * Draws a graph given in the DOT language.
 *
 * Laying the graph out happens on the page's own thread and holds it until it
 * is done: seconds for a graph of thousands of nodes, during which the page
 * neither repaints nor responds. So the layout starts only after the browser
 * has painted what the caller shows meanwhile, and the caller is told when the
 * graph is drawn or could not be.
 *
 * Graphviz names the font it measured the text with; the text is drawn in the
 * app's own.
 */
export const Graph = ({
  dot,
  onDrawn,
  onFailed,
}: {
  dot: string;
  onDrawn?: () => void;
  onFailed?: (message: string) => void;
}) => {
  const id = useMemo(getId, []);
  // the callbacks of the latest render, without drawing again for their sake
  const callbacks = useRef({ onDrawn, onFailed });
  callbacks.current = { onDrawn, onFailed };

  useEffect(() => {
    let start: number | undefined;
    // a frame, then a task: by then the frame has been painted
    const frame = requestAnimationFrame(() => {
      start = window.setTimeout(() => {
        graphviz(`#${id}`)
          .onerror((message) => callbacks.current.onFailed?.(String(message)))
          .on("end", () => callbacks.current.onDrawn?.())
          .renderDot(dot);
      });
    });
    return () => {
      cancelAnimationFrame(frame);
      window.clearTimeout(start);
    };
  }, [id, dot]);

  return (
    <Box
      id={id}
      sx={(theme) => ({
        "& svg text": { fontFamily: theme.typography.fontFamily },
      })}
    />
  );
};
